/**
 * command-search.ts — Tìm lệnh trong bảng ⌘K (components/shared/command-palette.tsx).
 *
 * - Không phân biệt hoa thường / dấu tiếng Việt: "nhan vien" khớp "Nhân viên", "dang nhap" khớp "Đăng nhập".
 * - Gõ nhiều từ thì MỌI từ đều phải có mặt (ở nhãn, nhóm hoặc từ khoá) — "zalo oa" không ra "Zalo cá nhân".
 * - Xếp hạng: khớp đầu nhãn > khớp đầu một từ trong nhãn > nằm giữa nhãn > chỉ khớp nhóm / từ khoá.
 *   Cùng điểm thì giữ thứ tự gốc (thứ tự menu).
 */

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)
/** Phím bổ trợ hiển thị cạnh phím tắt: ⌘ trên Mac, Ctrl trên Windows / Linux. */
export const MOD_KEY_LABEL = isMac ? '⌘' : 'Ctrl'

/** Chữ thường, bỏ dấu tiếng Việt (kể cả đ → d). */
export function foldVi(s: string): string {
  return s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/g, 'd')
}

export interface Searchable {
  label: string
  /** Đường dẫn cha, vd "Đội ngũ › Nhân viên AI". */
  group?: string
  /** Từ đồng nghĩa / mô tả — chỉ để tìm, không hiển thị. */
  keywords?: string[]
}

/** Điểm khớp của một lệnh; -1 = không khớp. Truy vấn rỗng → 0 (mọi lệnh đều khớp). */
export function scoreCommand(item: Searchable, query: string): number {
  const tokens = foldVi(query).split(/\s+/).filter(Boolean)
  if (!tokens.length) return 0
  const label = foldVi(item.label)
  const rest = foldVi([item.group ?? '', ...(item.keywords ?? [])].join(' '))
  const words = label.split(/[\s›/&(),.-]+/).filter(Boolean)

  let score = label.startsWith(tokens.join(' ')) ? 100 : 0
  for (const t of tokens) {
    if (words.some((w) => w.startsWith(t))) score += 10
    else if (label.includes(t)) score += 5
    else if (rest.includes(t)) score += 1
    else return -1
  }
  return score
}

/** Lệnh khớp truy vấn, điểm cao trước; truy vấn rỗng → nguyên danh sách. */
export function searchCommands<T extends Searchable>(items: T[], query: string): T[] {
  if (!query.trim()) return items
  return items
    .map((item, i) => ({ item, i, score: scoreCommand(item, query) }))
    .filter((x) => x.score >= 0)
    .sort((a, b) => b.score - a.score || a.i - b.i)
    .map((x) => x.item)
}
